import { CATALOG_SOURCE_LABELS } from '../searchRelatedProducts'
import { OUTLET_SHOP_ID } from '../outlet/outletCategoryConstants'

const SOURCE_CATALOG = 'catalog'
const SOURCE_CUSTOM = 'custom'

/** Онлайн-склад Premium після syncRemains. Не 601. */
export const PREMIUM_ONLINE_SHOP_ID = '843'

export const GIFT_FUND_CATALOG_SELECTION_KIND = {
  products: 'products',
  category: 'category',
  brand: 'brand',
}

export const GIFT_FUND_REMAINS_STATUS = {
  idle: 'idle',
  loading: 'loading',
  ready: 'ready',
  error: 'error',
}

export const createEmptyCatalogSelection = (kind = GIFT_FUND_CATALOG_SELECTION_KIND.products) => ({
  kind:
    kind === GIFT_FUND_CATALOG_SELECTION_KIND.category
      ? GIFT_FUND_CATALOG_SELECTION_KIND.category
      : kind === GIFT_FUND_CATALOG_SELECTION_KIND.brand
        ? GIFT_FUND_CATALOG_SELECTION_KIND.brand
        : GIFT_FUND_CATALOG_SELECTION_KIND.products,
  productRefs: [],
  category: null,
  brand: null,
  excludedProductRefs: [],
})

export const catalogProductKey = (source, id) => `${source || ''}:${id || ''}`

export const toCatalogProductRef = (product = {}) => ({
  source: product.source === 'outlet' ? 'outlet' : 'premium',
  id: String(product.id || product.catalogProductId || ''),
  title: String(product.title || product.name || product.catalogTitle || '').trim() || null,
  barcode: String(product.barcode || product.catalogBarcode || '').trim() || null,
  brandName: String(product.brandName || product.catalogBrandName || '').trim() || null,
})

const normalizeRef = (ref) => {
  if (!ref || !ref.id) {
    return null
  }

  const source = ref.source === 'outlet' ? 'outlet' : 'premium'
  const id = String(ref.id).trim()
  if (!id) {
    return null
  }

  return {
    source,
    id,
    title: String(ref.title || '').trim() || null,
    barcode: String(ref.barcode || '').trim() || null,
    brandName: String(ref.brandName || '').trim() || null,
  }
}

const uniqueRefs = (refs = []) => {
  const seen = new Set()
  const next = []
  refs.forEach((ref) => {
    const normalized = normalizeRef(ref)
    if (!normalized) {
      return
    }
    const key = catalogProductKey(normalized.source, normalized.id)
    if (seen.has(key)) {
      return
    }
    seen.add(key)
    next.push(normalized)
  })
  return next
}

export const normalizeCatalogSelection = (selection) => {
  if (!selection || typeof selection !== 'object') {
    return createEmptyCatalogSelection()
  }

  const kind =
    selection.kind === GIFT_FUND_CATALOG_SELECTION_KIND.category
      ? GIFT_FUND_CATALOG_SELECTION_KIND.category
      : selection.kind === GIFT_FUND_CATALOG_SELECTION_KIND.brand
        ? GIFT_FUND_CATALOG_SELECTION_KIND.brand
        : GIFT_FUND_CATALOG_SELECTION_KIND.products

  const category =
    selection.category && selection.category.catalogTaxonomyNodeId
      ? {
          source: 'catalogTaxonomy',
          catalogTaxonomyNodeId: String(selection.category.catalogTaxonomyNodeId),
          catalogTaxonomyNodeName: String(selection.category.catalogTaxonomyNodeName || ''),
          pathLabel: String(selection.category.pathLabel || ''),
          pathIds: Array.isArray(selection.category.pathIds) ? selection.category.pathIds.map(String) : [],
        }
      : null

  const brand =
    selection.brand && selection.brand.brandId
      ? {
          brandId: String(selection.brand.brandId),
          brandName: String(selection.brand.brandName || '').trim() || '',
        }
      : null

  return {
    kind,
    productRefs: uniqueRefs(selection.productRefs),
    category,
    brand,
    excludedProductRefs: uniqueRefs(selection.excludedProductRefs),
  }
}

/** Старі одиночні картки без catalogSelection → список з одного ref. */
export const getCatalogSelectionFromItem = (item) => {
  if (!item || item.sourceType !== SOURCE_CATALOG) {
    return createEmptyCatalogSelection()
  }

  if (item.catalogSelection && typeof item.catalogSelection === 'object') {
    const normalized = normalizeCatalogSelection(item.catalogSelection)
    if (normalized.kind === GIFT_FUND_CATALOG_SELECTION_KIND.category) {
      return normalized
    }
    if (normalized.kind === GIFT_FUND_CATALOG_SELECTION_KIND.brand) {
      return normalized
    }
    if (normalized.productRefs.length > 0) {
      return normalized
    }
  }

  if (item.catalogProductId && item.catalogSource) {
    return normalizeCatalogSelection({
      kind: GIFT_FUND_CATALOG_SELECTION_KIND.products,
      productRefs: [
        {
          source: item.catalogSource,
          id: item.catalogProductId,
          title: item.catalogTitle,
          barcode: item.catalogBarcode,
          brandName: item.catalogBrandName,
        },
      ],
    })
  }

  return createEmptyCatalogSelection()
}

export const isGiftFundCatalogPoolDraft = (item) => {
  if (!item || item.sourceType !== SOURCE_CATALOG) {
    return false
  }

  const selection = getCatalogSelectionFromItem(item)
  if (
    selection.kind === GIFT_FUND_CATALOG_SELECTION_KIND.category ||
    selection.kind === GIFT_FUND_CATALOG_SELECTION_KIND.brand
  ) {
    return true
  }

  return selection.productRefs.length > 1
}

export const getOnlineShopIdForCatalogSource = (catalogSource) =>
  catalogSource === 'outlet' ? String(OUTLET_SHOP_ID) : PREMIUM_ONLINE_SHOP_ID

/**
 * Live-наявність з shops/843 (premium) або shops/854 (outlet).
 * status !== ready → quantity null (не 0).
 */
export const getOnlineCatalogRemainQuantity = (
  productId,
  catalogSource,
  remainsByShop,
  remainsStatus = GIFT_FUND_REMAINS_STATUS.idle
) => {
  if (remainsStatus !== GIFT_FUND_REMAINS_STATUS.ready || !remainsByShop) {
    return { status: remainsStatus || GIFT_FUND_REMAINS_STATUS.idle, quantity: null }
  }

  const shopId = getOnlineShopIdForCatalogSource(catalogSource)
  const list = remainsByShop[shopId]
  if (!Array.isArray(list)) {
    return { status: GIFT_FUND_REMAINS_STATUS.error, quantity: null }
  }

  const row = list.find((item) => String(item?.productId) === String(productId))
  const raw = Number(row?.quantity)
  return {
    status: GIFT_FUND_REMAINS_STATUS.ready,
    quantity: Number.isFinite(raw) && raw > 0 ? raw : 0,
  }
}

export const isCatalogProductOfferable = (product, remain) => {
  if (!product || product.isArchived === true || product.isActive !== true) {
    return false
  }

  if (remain?.status !== GIFT_FUND_REMAINS_STATUS.ready) {
    return false
  }

  return Number(remain.quantity) > 0
}

export const productBelongsToTaxonomyNode = (product, nodeId) => {
  if (!nodeId || !product) {
    return false
  }

  const membership = Array.isArray(product.catalogTaxonomyMembershipIds)
    ? product.catalogTaxonomyMembershipIds
    : []
  return membership.some((id) => String(id) === String(nodeId))
}

export const resolveCategoryPoolMembers = (products = [], nodeId) => {
  if (!nodeId) {
    return []
  }

  return products.filter((product) => productBelongsToTaxonomyNode(product, nodeId))
}

export const resolveBrandPoolMembers = (products = [], brandId) => {
  if (!brandId) {
    return []
  }

  return products.filter((product) => String(product?.brandId || '') === String(brandId))
}

/** Зміна kind не чіпає бренд, категорію, SKU і винятки. */
export const withCatalogSelectionKind = (selection, kind) => {
  const normalized = normalizeCatalogSelection(selection)
  const nextKind =
    kind === GIFT_FUND_CATALOG_SELECTION_KIND.category
      ? GIFT_FUND_CATALOG_SELECTION_KIND.category
      : kind === GIFT_FUND_CATALOG_SELECTION_KIND.brand
        ? GIFT_FUND_CATALOG_SELECTION_KIND.brand
        : GIFT_FUND_CATALOG_SELECTION_KIND.products

  return {
    ...normalized,
    kind: nextKind,
  }
}

export const productMatchesPoolSearchQuery = (product, query) => {
  const needle = String(query || '').trim().toLowerCase()
  if (!needle) {
    return true
  }

  const haystack = [product?.name, product?.title, product?.barcode]
    .filter(Boolean)
    .join(' ')
    .toLowerCase()
  return haystack.includes(needle)
}

export const toggleCatalogPoolParticipation = (excludedProductRefs, product, participating) => {
  const ref = toCatalogProductRef({ ...product, source: 'premium' })
  if (!ref.id) {
    return uniqueRefs(excludedProductRefs)
  }

  if (participating) {
    return uniqueRefs(excludedProductRefs).filter(
      (row) => catalogProductKey(row.source, row.id) !== catalogProductKey(ref.source, ref.id)
    )
  }

  return uniqueRefs([...(excludedProductRefs || []), ref])
}

const ukCountUnit = (count, one, few, many) => {
  const abs = Math.abs(count) % 100
  const digit = abs % 10
  if (abs < 11 || abs > 14) {
    if (digit === 1) return one
    if (digit >= 2 && digit <= 4) return few
  }
  return many
}

export const formatGiftFundSkuCountLabel = (count) => {
  const n = Number(count)
  if (!Number.isInteger(n) || n < 0) {
    return '—'
  }

  return `${n} ${ukCountUnit(n, 'товар', 'товари', 'товарів')}`
}

export const formatGiftFundOfferCountLabel = (count) => {
  const n = Number(count)
  if (!Number.isInteger(n) || n < 0) {
    return '—'
  }

  return `${n} ${ukCountUnit(n, 'пропозиція', 'пропозиції', 'пропозицій')}`
}

export const GIFT_FUND_BRAND_PARTICIPATION_FILTER = {
  all: 'all',
  participating: 'participating',
  excluded: 'excluded',
}

export const GIFT_FUND_BRAND_REMAIN_FILTER = {
  all: 'all',
  inStock: 'inStock',
  outOfStock: 'outOfStock',
}

export const filterBrandPoolMembers = ({
  members = [],
  excludedProductRefs = [],
  remainsByShop = null,
  remainsStatus = GIFT_FUND_REMAINS_STATUS.idle,
  searchQuery = '',
  participationFilter = GIFT_FUND_BRAND_PARTICIPATION_FILTER.all,
  remainFilter = GIFT_FUND_BRAND_REMAIN_FILTER.all,
} = {}) => {
  const exclusionKeys = new Set(
    uniqueRefs(excludedProductRefs).map((ref) => catalogProductKey(ref.source, ref.id))
  )

  return members.filter((product) => {
    if (!productMatchesPoolSearchQuery(product, searchQuery)) {
      return false
    }

    const excluded = exclusionKeys.has(catalogProductKey('premium', product.id))
    if (participationFilter === GIFT_FUND_BRAND_PARTICIPATION_FILTER.participating && excluded) {
      return false
    }
    if (participationFilter === GIFT_FUND_BRAND_PARTICIPATION_FILTER.excluded && !excluded) {
      return false
    }

    const remain = getOnlineCatalogRemainQuantity(product.id, 'premium', remainsByShop, remainsStatus)
    if (remainFilter === GIFT_FUND_BRAND_REMAIN_FILTER.inStock) {
      return remain.status === GIFT_FUND_REMAINS_STATUS.ready && Number(remain.quantity) > 0
    }
    if (remainFilter === GIFT_FUND_BRAND_REMAIN_FILTER.outOfStock) {
      return remain.status === GIFT_FUND_REMAINS_STATUS.ready && Number(remain.quantity) <= 0
    }

    return true
  })
}

export const getCategoryPoolCounts = ({
  members = [],
  excludedProductRefs = [],
  remainsByShop = null,
  remainsStatus = GIFT_FUND_REMAINS_STATUS.idle,
} = {}) => {
  const exclusionKeys = new Set(
    uniqueRefs(excludedProductRefs).map((ref) => catalogProductKey(ref.source, ref.id))
  )
  const memberKey = (product) => catalogProductKey('premium', product.id)

  const inCategory = members.length
  const excludedInCategory = members.filter((product) => exclusionKeys.has(memberKey(product))).length
  const afterExclusions = members.filter((product) => !exclusionKeys.has(memberKey(product))).length

  const offerable =
    remainsStatus === GIFT_FUND_REMAINS_STATUS.ready
      ? members.filter((product) => {
          if (exclusionKeys.has(memberKey(product))) {
            return false
          }
          const remain = getOnlineCatalogRemainQuantity(
            product.id,
            'premium',
            remainsByShop,
            remainsStatus
          )
          return isCatalogProductOfferable(product, remain)
        }).length
      : null

  return {
    inCategory,
    excludedInCategory,
    afterExclusions,
    offerable,
    inPool: inCategory,
    excludedInPool: excludedInCategory,
  }
}

export const getProductsPoolOfferableCount = ({
  productRefs = [],
  productsByKey = {},
  remainsByShop = null,
  remainsStatus = GIFT_FUND_REMAINS_STATUS.idle,
} = {}) => {
  if (remainsStatus !== GIFT_FUND_REMAINS_STATUS.ready) {
    return null
  }

  return uniqueRefs(productRefs).filter((ref) => {
    const product = productsByKey[catalogProductKey(ref.source, ref.id)]
    const remain = getOnlineCatalogRemainQuantity(ref.id, ref.source, remainsByShop, remainsStatus)
    return isCatalogProductOfferable(product, remain)
  }).length
}

const getPoolOfferableValue = (counts, remainsStatus) =>
  remainsStatus === GIFT_FUND_REMAINS_STATUS.ready && counts.offerable != null ? counts.offerable : null

export const getGiftFundCatalogPoolTablePresentation = (
  item,
  { remainsByShop = null, remainsStatus = GIFT_FUND_REMAINS_STATUS.idle, productsByKey = {}, products = [] } = {}
) => {
  if (!item || item.sourceType === SOURCE_CUSTOM) {
    const quantity = Number.isInteger(item?.quantity) ? item.quantity : 0
    return {
      remainLabel: `${quantity} шт.`,
      remainValue: quantity,
      secondary: '',
      useSetAvatar: false,
    }
  }

  const selection = getCatalogSelectionFromItem(item)

  if (selection.kind === GIFT_FUND_CATALOG_SELECTION_KIND.category) {
    const members = resolveCategoryPoolMembers(products, selection.category?.catalogTaxonomyNodeId)
    const counts = getCategoryPoolCounts({
      members,
      excludedProductRefs: selection.excludedProductRefs,
      remainsByShop,
      remainsStatus,
    })
    const remainValue = getPoolOfferableValue(counts, remainsStatus)
    return {
      remainLabel: remainValue == null ? '—' : formatGiftFundSkuCountLabel(remainValue),
      remainValue,
      secondary: '',
      useSetAvatar: false,
    }
  }

  if (selection.kind === GIFT_FUND_CATALOG_SELECTION_KIND.brand) {
    const members = resolveBrandPoolMembers(products, selection.brand?.brandId)
    const counts = getCategoryPoolCounts({
      members,
      excludedProductRefs: selection.excludedProductRefs,
      remainsByShop,
      remainsStatus,
    })
    const remainValue = getPoolOfferableValue(counts, remainsStatus)
    return {
      remainLabel: remainValue == null ? '—' : formatGiftFundSkuCountLabel(remainValue),
      remainValue,
      secondary: `Бренд · виключено ${formatGiftFundSkuCountLabel(counts.excludedInPool)}`,
      useSetAvatar: true,
    }
  }

  if (selection.productRefs.length > 1) {
    const offerable =
      remainsStatus === GIFT_FUND_REMAINS_STATUS.ready
        ? getProductsPoolOfferableCount({
            productRefs: selection.productRefs,
            productsByKey,
            remainsByShop,
            remainsStatus,
          })
        : null
    return {
      remainLabel: offerable == null ? '—' : formatGiftFundSkuCountLabel(offerable),
      remainValue: offerable,
      secondary: '',
      useSetAvatar: false,
    }
  }

  const productId = selection.productRefs[0]?.id || item.catalogProductId
  const source = selection.productRefs[0]?.source || item.catalogSource
  const remain = getOnlineCatalogRemainQuantity(productId, source, remainsByShop, remainsStatus)
  if (remain.status !== GIFT_FUND_REMAINS_STATUS.ready || remain.quantity == null) {
    return {
      remainLabel: '—',
      remainValue: null,
      secondary: '',
      useSetAvatar: false,
    }
  }

  return {
    remainLabel: `${remain.quantity} шт.`,
    remainValue: remain.quantity,
    secondary: '',
    useSetAvatar: false,
  }
}

export const getGiftFundTableRemainLabel = (item, context) =>
  getGiftFundCatalogPoolTablePresentation(item, context).remainLabel

export const getGiftFundCatalogDisplayTitle = (item) => {
  const selection = getCatalogSelectionFromItem(item)
  if (selection.kind === GIFT_FUND_CATALOG_SELECTION_KIND.category) {
    return selection.category?.pathLabel || selection.category?.catalogTaxonomyNodeName || 'Категорія'
  }
  if (selection.kind === GIFT_FUND_CATALOG_SELECTION_KIND.brand) {
    const brandName = String(selection.brand?.brandName || '').trim()
    return brandName ? `Асортимент ${brandName}` : 'Асортимент'
  }
  if (selection.productRefs.length > 1) {
    return formatGiftFundSkuCountLabel(selection.productRefs.length)
  }
  return selection.productRefs[0]?.title || item?.catalogTitle || item?.catalogProductId || '—'
}

export const getGiftFundCatalogTypeSecondary = (item) => {
  const selection = getCatalogSelectionFromItem(item)
  if (selection.kind === GIFT_FUND_CATALOG_SELECTION_KIND.category) {
    return 'Категорія'
  }
  if (selection.kind === GIFT_FUND_CATALOG_SELECTION_KIND.brand) {
    return 'Бренд'
  }
  if (selection.productRefs.length > 1) {
    return formatGiftFundSkuCountLabel(selection.productRefs.length)
  }
  const source = selection.productRefs[0]?.source || item?.catalogSource
  return CATALOG_SOURCE_LABELS[source] || source || null
}

export const validateGiftFundCatalogSelection = (item) => {
  const errors = []
  const selection = getCatalogSelectionFromItem(item)

  if (selection.kind === GIFT_FUND_CATALOG_SELECTION_KIND.category) {
    if (!selection.category?.catalogTaxonomyNodeId) {
      errors.push('Оберіть категорію')
    }
    return { ok: errors.length === 0, errors }
  }

  if (selection.kind === GIFT_FUND_CATALOG_SELECTION_KIND.brand) {
    if (!selection.brand?.brandId) {
      errors.push('Оберіть бренд')
    }
    return { ok: errors.length === 0, errors }
  }

  if (selection.productRefs.length === 0) {
    errors.push('Оберіть товар із каталогу')
  }

  return { ok: errors.length === 0, errors }
}

export const buildCatalogProductLookup = (products = [], outletProducts = []) => {
  const productsByKey = {}
  products.forEach((product) => {
    if (product?.id) {
      productsByKey[catalogProductKey('premium', product.id)] = product
    }
  })
  outletProducts.forEach((product) => {
    if (product?.id) {
      productsByKey[catalogProductKey('outlet', product.id)] = product
    }
  })
  return productsByKey
}
